import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { PageShell } from "@project/user/components/page-shell";
import {
  Reveal,
  CountUp,
  ImageReveal,
  MotionPath,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { AboutInitiativesCarousel } from "@project/user/components/about-initiatives-carousel";
import { AwardsSection } from "@project/user/components/awards-section";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@project/components/ui/dialog";
import { useI18n } from "@project/lib/use-i18n";
import {
  Heart,
  Users,
  Building2,
  Sparkles,
  Quote,
  BadgeCheck,
  ShieldCheck,
  Flower2,
  HandHeart,
  Calendar,
  BookOpenText,
  ArrowRight,
  Rocket,
  Hospital,
  HeartPulse,
  Accessibility,
} from "lucide-react";
import careImg from "@project/assets/home-care.webp";
import medicalImg from "@project/assets/home-medical.webp";
import comfortImg from "@project/assets/image (1).jfif";
import { GALLERY_BY_CATEGORY } from "@project/lib/gallery-images";
import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import { galleryService } from "@project/services/admin/gallery.service";
import { resolveMediaUrl } from "@project/lib/utils";

const TRIPTYCH_IMAGES = {
  comfort: comfortImg,
  health: medicalImg,
  spirituality: GALLERY_BY_CATEGORY.derasar[0]?.src ?? careImg,
} as const;

const STATS_FOUNDED_YEAR = 2002;

export default function AboutPage() {
  const { t, lang } = useI18n();
  const [dynamicGallery, setDynamicGallery] = useState<{ src: string; title: string; desc?: string }[]>([]);

  useEffect(() => {
    let ignore = false;
    const loadAboutGallery = async () => {
      try {
        const catRes = await galleryCategoryService.listPage({ page: 1, page_size: 10 });
        const categories = (catRes.data || []).filter(c => c.active !== false);

        if (categories.length > 0) {
          // Fetch one or more images from top active categories
          const results = await Promise.all(
            categories.slice(0, 3).map(async cat => {
              try {
                const itemRes = await galleryService.listPage({
                  category: cat.id,
                  page: 1,
                  page_size: 1,
                });
                const firstItem = (itemRes.data || []).find(i => i.active !== false);
                if (!firstItem) return null;
                const src = resolveMediaUrl(firstItem.image_url || firstItem.image);
                if (!src) return null;
                return {
                  src,
                  title: firstItem.title || cat.category_name,
                  desc: firstItem.description || "",
                };
              } catch {
                return null;
              }
            })
          );
          const valid = results.filter((r): r is { src: string; title: string; desc: string } => r !== null);
          if (!ignore && valid.length > 0) {
            setDynamicGallery(valid);
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic about gallery", err);
      }
    };
    loadAboutGallery();
    return () => {
      ignore = true;
    };
  }, []);

  const staticAboutGallery = [
    {
      src: GALLERY_BY_CATEGORY["festival-celebrations"][0]?.src ?? careImg,
      title: "Festivals & Celebrations",
      desc: "Joyous gatherings, cultural rituals, and festive celebrations.",
    },
    {
      src: GALLERY_BY_CATEGORY.derasar[0]?.src ?? comfortImg,
      title: "Spiritual Derasar Sanctuary",
      desc: "Daily prayers, peaceful meditation, and spiritual serenity.",
    },
    {
      src: GALLERY_BY_CATEGORY["outdoor-activities"][0]?.src ?? medicalImg,
      title: "Community & Recreation",
      desc: "Engaging outdoor activities and joyful companionship.",
    },
  ];

  return (
    <PageShell>
      {/* ============== MAGAZINE HERO ============== */}
      <section className="relative overflow-hidden bg-background">
        <div className="bg-primary/10 pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full blur-3xl" />
        <div className="bg-gold/15 pointer-events-none absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full blur-3xl" />

        <div className="relative mx-auto grid max-w-site items-center gap-12 px-4 pb-16 pt-14 md:grid-cols-12 md:py-24">
          {/* Editorial label + headline */}
          <div className="md:col-span-7">
            <div className="anim-fade-up" style={{ animationDelay: "100ms" }}>
              <div className="border-primary/20 bg-primary/5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-primary">
                <Sparkles className="h-3.5 w-3.5 text-gold" />{" "}
                {t("pages.about.hero.badge")}
              </div>
            </div>
            <h1 className="mt-6 font-serif italic text-foreground">
              <span className="anim-line" style={{ animationDelay: "240ms" }}>
                <span>
                  {t("pages.about.hero.titleStart")}
                  <span className="not-italic text-primary">
                    {t("pages.about.hero.titleHighlight")}
                  </span>
                  {t("pages.about.hero.titleEnd")}
                </span>
              </span>
            </h1>
            <div className="anim-fade-up" style={{ animationDelay: "420ms" }}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                {t("pages.about.hero.description")}
              </p>
            </div>
            <div className="anim-fade-up" style={{ animationDelay: "560ms" }}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="hover:bg-primary/90 btn-lift rounded-full bg-primary text-primary-foreground"
                >
                  <Link to="/donate">
                    <HandHeart className="mr-2 h-4 w-4" />{" "}
                    {t("pages.about.hero.supportCause")}
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-primary/30 btn-lift rounded-full bg-background text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <Link to="/contact">
                    <Calendar className="mr-2 h-4 w-4" />{" "}
                    {t("pages.about.hero.visitHome")}
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Pull-quote card (Poem Preview & Full Poem Dialog) */}
          <div
            className="anim-zoom-in md:col-span-5"
            style={{ animationDelay: "360ms" }}
          >
            <figure className="shadow-foreground/5 relative flex h-full min-h-[380px] flex-col justify-between rounded-3xl border border-border bg-card p-7 shadow-xl sm:p-9">
              <div className="absolute -top-5 left-7 grid h-10 w-10 place-items-center rounded-full bg-gold text-gold-foreground shadow">
                <Quote className="h-5 w-5" />
              </div>

              <div className="mt-4 flex-1">
                {/* Intro Line */}
                <p className="mb-4 whitespace-pre-line !text-[1.2rem] italic leading-snug text-primary">
                  {t("pages.about.hero.poem.intro")}
                </p>

                {/* 3 Line Preview of Poem */}
                <div className="border-gold/40 space-y-3 border-l-2 py-1 pl-4">
                  {(() => {
                    const rawLines = t("pages.about.hero.poem.lines", {
                      returnObjects: true,
                    });
                    const lines = Array.isArray(rawLines)
                      ? (rawLines as string[])
                      : [];
                    return lines.slice(0, 3).map(line => (
                      <p
                        key={line}
                        className="!text-[1rem] italic !leading-[1.3] leading-relaxed text-foreground"
                      >
                        {line}
                      </p>
                    ));
                  })()}
                  <div className="mt-1 flex items-center gap-2 font-serif !text-[1.2rem] italic text-muted-foreground">
                    <span>...</span>
                    {/* Read More Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="link"
                          className="mt-2 flex h-auto cursor-pointer items-center gap-1 p-0 font-sans text-xs font-semibold not-italic text-primary underline transition-colors hover:text-gold"
                        >
                          {t("pages.about.hero.poem.readMore")}
                          {/* <ArrowRight className="h-3 w-3" /> */}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden border-border bg-card p-0">
                        <DialogHeader className="bg-muted/30 border-b border-border p-6 pb-4">
                          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-primary">
                            <Quote className="h-5 w-5 shrink-0 text-gold" />
                            {t("pages.about.hero.poem.title")}
                          </DialogTitle>
                        </DialogHeader>

                        <div className="flex-1 space-y-6 overflow-y-auto p-6 md:p-8">
                          <p className="border-border/60 whitespace-pre-line border-b pb-4 !text-[1.1rem] text-lg italic !leading-[1.5] text-primary">
                            {t("pages.about.hero.poem.intro")}
                          </p>

                          <div className="border-gold/50 space-y-3 border-l-2 pl-4 text-base leading-relaxed text-foreground md:text-lg">
                            {(() => {
                              const rawLines = t(
                                "pages.about.hero.poem.lines",
                                {
                                  returnObjects: true,
                                }
                              );
                              const lines = Array.isArray(rawLines)
                                ? (rawLines as string[])
                                : [];
                              return lines.map(line => (
                                <p
                                  key={line}
                                  className="py-0.5 !text-[1rem] !leading-[1.3] transition-colors hover:text-primary"
                                >
                                  {line}
                                </p>
                              ));
                            })()}
                          </div>

                          <div className="bg-muted/20 mt-8 flex flex-col gap-4 rounded-xl border-t border-border p-4 pt-6 text-sm sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t("pages.about.hero.poem.authorLabel")}
                              </span>
                              <span className="mt-0.5 block font-serif text-base font-bold text-foreground">
                                {t("pages.about.hero.poem.authorName")}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Location / Foundation
                              </span>
                              <span className="mt-0.5 block font-serif text-base text-foreground">
                                {t("pages.about.hero.poem.foundation")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="text-sm font-semibold text-foreground">
                  {t("pages.about.hero.poem.authorName")}
                </div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {/* {t("pages.about.hero.poem.authorLabel")} */}
                  {t("pages.about.hero.poem.foundation")}
                </div>
              </figcaption>

              <div className="bg-primary/10 pointer-events-none absolute -bottom-2 -right-2 grid h-12 w-12 place-items-center rounded-2xl text-primary">
                <BadgeCheck className="h-6 w-6" />
              </div>
            </figure>
          </div>
        </div>
      </section>

      {/* ============== IMPACT NUMBERS ============== */}
      <section className="border-border/60 bg-card/40 border-y">
        <Stagger className="mx-auto grid max-w-site gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Building2,
              n: 298,
              suffix: "+",
              labelKey: "pages.about.stats.residents" as const,
            },
            {
              icon: Users,
              n: 27000,
              suffix: "+",
              labelKey: "pages.about.stats.donors" as const,
            },
            {
              icon: Heart,
              n: Math.max(1, new Date().getFullYear() - STATS_FOUNDED_YEAR),
              suffix: " Yrs",
              labelKey: "pages.about.stats.years" as const,
            },
            {
              icon: ShieldCheck,
              n: 100,
              suffix: "%",
              labelKey: "pages.about.stats.tax" as const,
            },
          ].map((s, i) => (
            <StaggerItem key={s.labelKey} index={i} from="zoom">
              <div className="flex items-center gap-3 rounded-2xl border border-border bg-white p-5">
                <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                  <s.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-serif text-2xl font-semibold text-foreground">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(s.labelKey)}
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <AboutInitiativesCarousel />

      {/* ============== OUR STORY (magazine columns) ============== */}
      <section className="mx-auto max-w-site px-4 py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal variant="left" className="md:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.story.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              {t("pages.about.story.heading")}
            </h2>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-primary">
              <BookOpenText className="h-3.5 w-3.5" />{" "}
              {t("pages.about.story.chronicled")}
            </div>
          </Reveal>

          <Reveal
            variant="fade"
            delay={180}
            className="space-y-6 text-base leading-relaxed text-muted-foreground md:col-span-8 md:columns-2 md:gap-8 md:[column-rule:1px_solid_var(--color-border)]"
          >
            <p className="break-inside-avoid">
              <span className="float-left mr-3 font-serif text-6xl leading-[0.85] text-primary">
                {t("pages.about.story.dropcap")}
              </span>
              {t("pages.about.story.p1a")}
              <strong className="text-foreground">
                {t("pages.about.story.p1bold")}
              </strong>
              {t("pages.about.story.p1b")}
            </p>
            <p className="break-inside-avoid">{t("pages.about.story.p2")}</p>
            <p className="break-inside-avoid">{t("pages.about.story.p3")}</p>
            <p className="break-inside-avoid">{t("pages.about.story.p4")}</p>
          </Reveal>
        </div>
      </section>

      {/* ============== MILESTONES TIMELINE ============== */}
      <section className="bg-secondary/40">
        <div className="mx-auto max-w-site px-4 py-20">
          <Reveal variant="fade" className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.milestones.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              <RevealText delay={120}>
                {t("pages.about.milestones.heading")}
              </RevealText>
            </h2>
          </Reveal>

          <ol className="relative mx-auto mt-14 max-w-5xl">
            <div
              aria-hidden
              className="absolute bottom-0 left-1/2 top-0 hidden w-0 -translate-x-1/2 md:block"
            >
              <MotionPath
                orientation="vertical"
                className="h-full w-0"
                lineClassName="!rounded-none border-l-2 border-dashed border-gold"
              />
            </div>
            {[
              {
                year: "2002",
                yearGu: "૨૦૦૨",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2002.title",
                    bodyKey: "pages.about.milestones.m2002.body",
                  },
                ],
              },
              {
                year: "2011",
                yearGu: "૨૦૧૧",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2011_gruh.title",
                    bodyKey: "pages.about.milestones.m2011_gruh.body",
                  },
                  {
                    titleKey: "pages.about.milestones.m2011_med.title",
                    bodyKey: "pages.about.milestones.m2011_med.body",
                  },
                ],
              },
              {
                year: "2012",
                yearGu: "૨૦૧૨",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2012.title",
                    bodyKey: "pages.about.milestones.m2012.body",
                  },
                ],
              },
              {
                year: "2014",
                yearGu: "૨૦૧૪",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2014.title",
                    bodyKey: "pages.about.milestones.m2014.body",
                  },
                ],
              },
              {
                year: "2015",
                yearGu: "૨૦૧૫",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2015.title",
                    bodyKey: "pages.about.milestones.m2015.body",
                  },
                ],
              },
              {
                year: "2016",
                yearGu: "૨૦૧૬",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2016.title",
                    bodyKey: "pages.about.milestones.m2016.body",
                  },
                ],
              },
              {
                year: "2017",
                yearGu: "૨૦૧૭",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2017.title",
                    bodyKey: "pages.about.milestones.m2017.body",
                  },
                ],
              },
              {
                year: "2018",
                yearGu: "૨૦૧૮",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2018.title",
                    bodyKey: "pages.about.milestones.m2018.body",
                  },
                ],
              },
              {
                year: "2022",
                yearGu: "૨૦૨૨",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2022.title",
                    bodyKey: "pages.about.milestones.m2022.body",
                  },
                ],
              },
              {
                year: "2024 & Beyond",
                yearGu: "૨૦૨૪ અને આગળ",
                items: [
                  {
                    titleKey: "pages.about.milestones.m2024_dental.title",
                    bodyKey: "pages.about.milestones.m2024_dental.body",
                  },
                  {
                    titleKey: "pages.about.milestones.m2024_beyond.title",
                    bodyKey: "pages.about.milestones.m2024_beyond.body",
                  },
                ],
              },
            ].map((m, i) => {
              const isLeft = i % 2 === 0;
              return (
                <Reveal
                  key={`${m.year}-${i}`}
                  variant={isLeft ? "left" : "right"}
                  delay={60}
                >
                  <li className="relative mb-4 last:mb-0 md:mb-5">
                    <span className="absolute left-1/2 top-8 z-10 hidden h-9 w-9 -translate-x-1/2 place-items-center rounded-full border-2 border-gold bg-primary text-[10px] font-bold text-primary-foreground md:grid">
                      {i + 1}
                    </span>
                    <div className="grid md:grid-cols-2 md:gap-12">
                      <div
                        className={
                          isLeft
                            ? "md:col-start-1 md:pr-12"
                            : "md:col-start-2 md:pl-12"
                        }
                      >
                        <div className="card-lift rounded-2xl border !border-[#efa93194] bg-card p-5 shadow-md">
                          <div className="flex items-baseline gap-3">
                            <span className="font-serif text-2xl text-primary">
                              {lang === "gu" ? m.yearGu : m.year}
                            </span>
                            <span className="h-px flex-1 bg-border" />
                          </div>
                          <div className="mt-2.5 space-y-4">
                            {m.items.map((item, itemIdx) => (
                              <div
                                key={item.titleKey}
                                className={
                                  itemIdx > 0
                                    ? "border-t border-border/60 pt-3"
                                    : ""
                                }
                              >
                                <h3 className="!font-sans !text-xl font-bold text-foreground">
                                  {t(item.titleKey)}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                  {t(item.bodyKey)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ol>

          {/* ---------- Upcoming projects (highlighted end of timeline) ---------- */}
          <Reveal variant="morph" className="relative mx-auto max-w-5xl">
            <div
              aria-hidden
              className="mx-auto hidden h-12 w-0 border-l-2 border-dashed border-gold md:block"
            />
            <div className="relative mt-10 rounded-3xl border-2 border-gold bg-gradient-to-br from-primary to-primary/85 p-6 pt-12 text-primary-foreground shadow-xl md:mt-6 md:p-10 md:pt-14">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl"
              >
                <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gold/20 blur-3xl" />
                <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
              </div>

              {/* timeline node sitting on the card's top border */}
              <span className="absolute left-1/2 top-0 z-10 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-gold bg-primary shadow-md">
                <Rocket className="h-5 w-5 text-white" />
              </span>

              <div className="relative flex flex-col items-center text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
                  {t("pages.about.milestones.upcoming.eyebrow")}
                </p>
                <h3 className="mt-2 font-serif text-3xl text-primary-foreground md:text-4xl">
                  {t("pages.about.milestones.upcoming.heading")}
                </h3>
                <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold px-4 py-1 text-sm font-bold text-primary">
                  <Calendar className="h-4 w-4" />
                  {t("pages.about.milestones.upcoming.years")}
                </span>
              </div>

              <Stagger
                className="relative mt-8 grid gap-4 md:grid-cols-2"
                delay={250}
              >
                {[
                  { icon: Building2, key: "vatsalyadham", hasBody: true },
                  { icon: Hospital, key: "hospital" },
                  { icon: HeartPulse, key: "cancerCare" },
                  { icon: Accessibility, key: "paralysisRehab" },
                ].map((p, i) => (
                  <StaggerItem key={p.key} index={i} className="h-full">
                    <div className="card-lift border-gold/40 flex h-full gap-4 rounded-2xl border bg-card p-2 text-left shadow-md">
                      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                        <p.icon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {/* <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-bold text-gold">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-gold">
                            {t("pages.about.milestones.upcoming.tag")}
                          </span>
                        </div> */}
                        <h4 className="mt-1 !font-sans !text-base font-bold !text-foreground md:!text-lg">
                          {t(`pages.about.milestones.upcoming.${p.key}.title`)}
                        </h4>
                        {p.hasBody && (
                          <p className=" !text-[10.5px] leading-relaxed text-muted-foreground">
                            {t(`pages.about.milestones.upcoming.${p.key}.body`)}
                          </p>
                        )}
                      </div>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============== AWARDS ============== */}
      <AwardsSection />

      {/* ============== MISSION · VISION · VALUES ============== */}
      <section className="mx-auto max-w-site px-4 py-20">
        <Stagger className="grid gap-6 md:grid-cols-3" step={150}>
          {[
            {
              icon: Heart,
              tagKey: "pages.about.mvv.mission.tag",
              titleKey: "pages.about.mvv.mission.title",
              bodyKey: "pages.about.mvv.mission.body",
            },
            {
              icon: Flower2,
              tagKey: "pages.about.mvv.vision.tag",
              titleKey: "pages.about.mvv.vision.title",
              bodyKey: "pages.about.mvv.vision.body",
            },
            {
              icon: ShieldCheck,
              tagKey: "pages.about.mvv.values.tag",
              titleKey: "pages.about.mvv.values.title",
              bodyKey: "pages.about.mvv.values.body",
            },
          ].map((c, i) => (
            <StaggerItem key={c.tagKey} index={i} className="h-full">
              <article className="card-lift group flex h-full flex-col rounded-3xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="shadow-primary/20 grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                    {t(c.tagKey)}
                  </span>
                </div>
                <h3 className="mt-5 font-serif text-foreground">
                  {t(c.titleKey)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t(c.bodyKey)}
                </p>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* ============== EDITORIAL TRIPTYCH ============== */}
      <section className="bg-card/40 border-border/60 -mb-20 border-y">
        <div className="mx-auto max-w-site px-4 py-20">
          <Reveal variant="morph" className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.triptych.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              <RevealText delay={120}>
                {t("pages.about.triptych.heading")}
              </RevealText>
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("pages.about.triptych.description")}
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 md:grid-cols-12">
            <Reveal variant="fade" className="md:col-span-7">
              <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                <ImageReveal direction="left">
                  <img
                    src={TRIPTYCH_IMAGES.comfort}
                    alt={t("pages.about.triptych.careAlt")}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </ImageReveal>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 text-primary-foreground">
                  <div className="text-gold/90 text-xs uppercase tracking-[0.22em]">
                    {t("pages.about.triptych.morningsLabel")}
                  </div>
                  <div className="mt-1 font-serif text-2xl">
                    {t("pages.about.triptych.morningsText")}
                  </div>
                </figcaption>
              </figure>
            </Reveal>

            <div className="grid gap-4 md:col-span-5">
              <Reveal variant="fade" delay={120}>
                <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                  <ImageReveal direction="right" delay={150}>
                    <img
                      src={TRIPTYCH_IMAGES.health}
                      alt={t("pages.about.triptych.medicalAlt")}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </ImageReveal>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-primary-foreground">
                    <div className="text-gold/90 text-[10px] uppercase tracking-[0.22em]">
                      {t("pages.about.triptych.afternoonsLabel")}
                    </div>
                    <div className="mt-1 font-serif text-lg">
                      {t("pages.about.triptych.afternoonsText")}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
              <Reveal variant="fade" delay={220}>
                <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                  <ImageReveal direction="up" delay={280}>
                    <img
                      src={TRIPTYCH_IMAGES.spirituality}
                      alt={t("pages.about.triptych.mealAlt")}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </ImageReveal>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-primary-foreground">
                    <div className="text-gold/90 text-[10px] uppercase tracking-[0.22em]">
                      {t("pages.about.triptych.eveningsLabel")}
                    </div>
                    <div className="mt-1 font-serif text-lg">
                      {t("pages.about.triptych.eveningsText")}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Glimpses Gallery ============ */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            PHOTO GALLERY
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Glimpses of Life at Jivandhara
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Explore moments of joy, spiritual peace, and community living at our sanctuary.
          </p>
        </Reveal>

        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(dynamicGallery.length > 0
            ? dynamicGallery
            : staticAboutGallery
          ).slice(0, 3).map((g, i) => (
            <StaggerItem key={g.title + i} index={i}>
              <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <ImageReveal direction="center" delay={i * 120}>
                  <img
                    src={g.src}
                    alt={g.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </ImageReveal>
                <div className="from-primary/90 via-primary/30 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent opacity-80 transition-opacity group-hover:opacity-95" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <h3 className="font-serif text-lg font-semibold">{g.title}</h3>
                  {g.desc && <p className="mt-1 text-xs text-white/80">{g.desc}</p>}
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-8 text-center">
          <Button
            asChild
            variant="outline"
            className="border-primary/30 btn-lift rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/gallery">
              View Full Gallery <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </PageShell>
  );
}
